import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAuthenticated = Boolean(user && session);

  // Clear error on successful operations
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Update user and session state from service
  const updateAuthState = useCallback(() => {
    const currentUser = authService.getCurrentUser();
    const currentSession = authService.getCurrentSession();
    setUser(currentUser);
    setSession(currentSession);
  }, []);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        setError(null);
        
        // Wait for service initialization
        await authService.waitForInit();
        
        // Validate current session
        const isValid = await authService.validateSession();
        
        if (isValid) {
          updateAuthState();
        } else {
          setUser(null);
          setSession(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
        setSession(null);
        setError('Failed to initialize authentication');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [updateAuthState]);

  // Register user
  const register = useCallback(async ({ name, email, password }) => {
    try {
      setError(null);
      const result = await authService.register({ name, email, password });
      
      if (result.session) {
        setUser(result.user);
        setSession(result.session);
      }
      
      return result;
    } catch (error) {
      setError(error.message || 'Registration failed');
      throw error;
    }
  }, []);

  // Login user
  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      const result = await authService.login(email, password);
      setUser(result.user);
      setSession(result.session);
      return result;
    } catch (error) {
      setError(error.message || 'Login failed');
      throw error;
    }
  }, []);

  // Logout user
  const logout = useCallback(async () => {
    try {
      setError(null);
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
      setError('Logout failed');
    } finally {
      setUser(null);
      setSession(null);
    }
  }, []);

  // Update user profile
  const updateProfile = useCallback(async (updates) => {
    try {
      setError(null);
      const updatedUser = await authService.updateProfile(updates);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      setError(error.message || 'Failed to update profile');
      throw error;
    }
  }, []);

  // Reset password
  const resetPassword = useCallback(async (email) => {
    try {
      setError(null);
      await authService.resetPassword(email);
    } catch (error) {
      setError(error.message || 'Failed to send reset email');
      throw error;
    }
  }, []);

  // Update password
  const updatePassword = useCallback(async (newPassword) => {
    try {
      setError(null);
      await authService.updatePassword(newPassword);
    } catch (error) {
      setError(error.message || 'Failed to update password');
      throw error;
    }
  }, []);

  // Get user profile metadata
  const getUserProfile = useCallback(() => {
    return authService.getUserProfile();
  }, []);

  // Check if email is confirmed
  const isEmailConfirmed = useCallback(() => {
    return authService.isEmailConfirmed();
  }, []);

  // Resend confirmation email
  const resendConfirmation = useCallback(async (email) => {
    try {
      setError(null);
      await authService.resendConfirmation(email);
    } catch (error) {
      setError(error.message || 'Failed to resend confirmation');
      throw error;
    }
  }, []);

  const value = {
    // State
    user,
    session,
    isLoading,
    isAuthenticated,
    error,
    
    // Actions
    register,
    login,
    logout,
    updateProfile,
    resetPassword,
    updatePassword,
    clearError,
    resendConfirmation,
    
    // Utilities
    getUserProfile,
    isEmailConfirmed,
    userType: 'viewer'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Backward compatibility exports
export const useAuthContext = useAuth;
export const useCreatorAuth = useAuth; // For existing components
export const CreatorAuthProvider = AuthProvider; // For existing components
export default AuthContext;